import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_URL } from '../../context/AuthContext';
import { EyeIcon, EyeSlashIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import AdminNav from './AdminNav';

const NIVELES_MILITAR = ['SLP2', 'SLP3', 'SLP4', 'ACCESO_SUBOFICIAL', 'ACCESO_OFICIAL', 'CURSO_SUBOFICIAL', 'CURSO_OFICIAL'];
const NIVELES_CIVIL   = ['B1', 'B2', 'C1', 'C2'];
const NIVEL_LABEL = { SLP1:'SLP 1', SLP2:'SLP 2', SLP3:'SLP 3', SLP4:'SLP 4', ACCESO_SUBOFICIAL:'Acceso Suboficial', ACCESO_OFICIAL:'Acceso Oficial', CURSO_SUBOFICIAL:'Curso Suboficial', CURSO_OFICIAL:'Curso Oficial', B1:'B1', B2:'B2', C1:'C1', C2:'C2' };

const usuarioBase = () => ({
  nombre: '', email: '', password: '', rol: 'alumno',
  activo: 1, suscripcion_activa: 0, accesos: [],
});

function toggleAcceso(accesos, tipo, nivel) {
  const existe = accesos.some(a => a.tipo === tipo && a.nivel === nivel);
  if (existe) return accesos.filter(a => !(a.tipo === tipo && a.nivel === nivel));
  return [...accesos, { tipo, nivel }];
}

const estaCongelado = (u) => u.congelado_hasta && new Date(u.congelado_hasta) > new Date();

export default function AdminUsuarios() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios]       = useState([]);
  const [filtro, setFiltro]           = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm]               = useState(usuarioBase());
  const [editandoId, setEditandoId]   = useState(null);
  const [error, setError]             = useState('');
  const [guardando, setGuardando]     = useState(false);
  const [verPassword, setVerPassword] = useState(false);
  const [esMobile, setEsMobile]       = useState(() => window.innerWidth <= 768);

  // Modal congelar
  const [congelarModal, setCongelarModal]   = useState(null);
  const [congelarHoras, setCongelarHoras]   = useState(24);
  const [congelarMotivo, setCongelarMotivo] = useState('');
  const [congelando, setCongelando]         = useState(false);

  // Modal GDPR
  const [gdprModal, setGdprModal]       = useState(null);
  const [gdprConfirm, setGdprConfirm]   = useState('');
  const [ejecutandoGdpr, setEjecutandoGdpr] = useState(false);
  const [gdprError, setGdprError]       = useState('');

  useEffect(() => {
    const check = () => setEsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (usuario?.rol !== 'admin') { navigate('/'); return; }
    cargar();
  }, []);

  const cargar = async () => {
    try { const res = await axios.get(`${API_URL}/admin/usuarios`); setUsuarios(res.data); } catch {}
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      if (editandoId) {
        await axios.put(`${API_URL}/admin/usuarios/${editandoId}`, form);
      } else {
        await axios.post(`${API_URL}/admin/usuarios`, form);
      }
      setForm(usuarioBase());
      setMostrarForm(false);
      setEditandoId(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const iniciarEdicion = (u) => {
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol, activo: u.activo, suscripcion_activa: u.suscripcion_activa, accesos: u.accesos || [] });
    setEditandoId(u.id);
    setMostrarForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try { await axios.delete(`${API_URL}/admin/usuarios/${id}`); cargar(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const toggleActivo = async (u) => {
    await axios.put(`${API_URL}/admin/usuarios/${u.id}`, { nombre: u.nombre, email: u.email, rol: u.rol, activo: u.activo ? 0 : 1, suscripcion_activa: u.suscripcion_activa, accesos: u.accesos || [] });
    cargar();
  };

  const toggleSuscripcion = async (u) => {
    await axios.put(`${API_URL}/admin/usuarios/${u.id}`, { nombre: u.nombre, email: u.email, rol: u.rol, activo: u.activo, suscripcion_activa: u.suscripcion_activa ? 0 : 1, accesos: u.accesos || [] });
    cargar();
  };

  const confirmarCongelar = async () => {
    setCongelando(true);
    try {
      await axios.post(`${API_URL}/admin/control/congelar/${congelarModal.id}`, { horas: congelarHoras, motivo: congelarMotivo });
      setCongelarModal(null);
      setCongelarMotivo('');
      setCongelarHoras(24);
      cargar();
    } catch (e) { alert(e.response?.data?.error || 'Error al congelar'); }
    finally { setCongelando(false); }
  };

  const descongelar = async (id) => {
    try { await axios.post(`${API_URL}/admin/control/descongelar/${id}`); cargar(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const forzarPassword = async (id, nombre) => {
    if (!window.confirm(`¿Forzar a ${nombre} a cambiar su contraseña en el próximo login?`)) return;
    try { await axios.post(`${API_URL}/admin/control/forzar-password/${id}`); cargar(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const ejecutarGDPR = async () => {
    setGdprError('');
    setEjecutandoGdpr(true);
    try {
      await axios.delete(`${API_URL}/admin/control/gdpr/${gdprModal.id}`, { data: { confirmar_email: gdprConfirm } });
      setGdprModal(null);
      setGdprConfirm('');
      cargar();
    } catch (e) {
      setGdprError(e.response?.data?.error || 'Error al borrar');
    } finally { setEjecutandoGdpr(false); }
  };

  const usuariosFiltrados = usuarios.filter(u =>
    u.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
    u.email.toLowerCase().includes(filtro.toLowerCase())
  );

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '10px', background: '#FFF0F5', border: '1px solid #FBCFE8', color: '#581C47', fontSize: '14px' };
  const btnGhost   = { background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' };
  const btnGrad    = { background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(236,72,153,0.35)' };

  const BadgeCongelado = ({ u }) => estaCongelado(u) ? (
    <span title={`Hasta ${new Date(u.congelado_hasta).toLocaleString('es-ES')}`} style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>🧊 Congelado</span>
  ) : null;

  const BadgeForzarPwd = ({ u }) => u.forzar_cambio_password ? (
    <span style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', borderRadius: '6px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>🔑 Cambio pendiente</span>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: '#FFF0F5' }}>
      <AdminNav esMobile={esMobile} tipo="subpagina" titulo={esMobile ? 'Usuarios' : 'Gestión de Usuarios'} />

      <div style={{ maxWidth: '1200px', padding: esMobile ? '16px 12px 80px' : '32px 24px' }} className="fade-in">
        {/* Header */}
        <div className="section-header" style={{ marginBottom: '20px' }}>
          <div>
            <h1 style={{ color: '#581C47', fontSize: esMobile ? '22px' : '26px', marginBottom: '4px' }}>👥 Usuarios</h1>
            <p style={{ color: '#BE84A3', fontSize: '13px' }}>{usuariosFiltrados.length} usuario(s)</p>
          </div>
          <button onClick={() => { setForm(usuarioBase()); setEditandoId(null); setMostrarForm(!mostrarForm); }} style={{ ...btnGrad, width: esMobile ? '100%' : 'auto' }}>
            {mostrarForm && !editandoId ? '✕ Cancelar' : '+ Nuevo usuario'}
          </button>
        </div>

        {/* Formulario */}
        {mostrarForm && (
          <form onSubmit={handleGuardar} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '16px', padding: esMobile ? '18px 16px' : '24px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(236,72,153,0.08)' }}>
            <h3 style={{ color: '#EC4899', marginBottom: '18px', fontSize: '16px' }}>{editandoId ? 'Editar usuario' : 'Crear nuevo usuario'}</h3>
            {error && <div style={{ background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.3)', borderRadius: '10px', padding: '10px', color: '#DB2777', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px', marginBottom: '14px' }}>
              {[{ label: 'Nombre *', key: 'nombre', type: 'text', req: true }, { label: 'Email *', key: 'email', type: 'email', req: true }].map(f => (
                <div key={f.key}>
                  <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '5px' }}>{f.label}</label>
                  <input type={f.type} style={inputStyle} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} required={f.req} />
                </div>
              ))}
              <div>
                <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '5px' }}>Contraseña {editandoId ? '(vacío = no cambiar)' : '*'}</label>
                <div style={{ position: 'relative' }}>
                  <input type={verPassword ? 'text' : 'password'} style={{ ...inputStyle, paddingRight: '40px' }} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editandoId} />
                  <button type="button" onClick={() => setVerPassword(v => !v)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#BE84A3', padding: 0, display: 'flex', alignItems: 'center' }}>
                    {verPassword ? <EyeSlashIcon style={{ width: 17, height: 17 }} /> : <EyeIcon style={{ width: 17, height: 17 }} />}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px', maxWidth: esMobile ? '100%' : '220px' }}>
              <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '5px' }}>Rol *</label>
              <select style={inputStyle} value={form.rol} onChange={e => setForm({ ...form, rol: e.target.value, accesos: [] })}>
                <option value="alumno">Alumno</option>
                <option value="profesor">Profesor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {form.rol === 'alumno' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Accesos (puede marcar varios)</label>
                <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  {[{ tipo: 'civil', label: '🎓 Civil', niveles: NIVELES_CIVIL }, { tipo: 'militar', label: '🎖️ Militar', niveles: NIVELES_MILITAR }].map(seccion => (
                    <div key={seccion.tipo} style={{ background: '#FFF0F5', border: '1px solid #FBCFE8', borderRadius: '10px', padding: '14px' }}>
                      <p style={{ color: '#581C47', fontWeight: 600, fontSize: '13px', marginBottom: '10px' }}>{seccion.label}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {seccion.niveles.map(n => {
                          const activo = form.accesos.some(a => a.tipo === seccion.tipo && a.nivel === n);
                          return (
                            <button key={n} type="button" onClick={() => setForm(prev => ({ ...prev, accesos: toggleAcceso(prev.accesos, seccion.tipo, n) }))} style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: activo ? '2px solid #EC4899' : '2px solid #FBCFE8', background: activo ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' : '#FFFFFF', color: activo ? '#fff' : '#BE84A3', transition: 'all 0.15s' }}>
                              {NIVEL_LABEL[n] || n}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {form.accesos.length === 0 && <p style={{ color: '#F59E0B', fontSize: '12px', marginTop: '8px' }}>⚠️ Sin accesos asignados — el alumno no podrá entrar a ninguna sección</p>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#EC4899', fontSize: '14px' }}>
                <input type="checkbox" checked={!!form.activo} onChange={e => setForm({ ...form, activo: e.target.checked ? 1 : 0 })} style={{ accentColor: '#EC4899', width: '16px', height: '16px' }} />
                Cuenta activa
              </label>
              {form.rol === 'alumno' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#EC4899', fontSize: '14px' }}>
                  <input type="checkbox" checked={!!form.suscripcion_activa} onChange={e => setForm({ ...form, suscripcion_activa: e.target.checked ? 1 : 0 })} style={{ accentColor: '#34D399', width: '16px', height: '16px' }} />
                  Suscripción activa
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="submit" disabled={guardando} style={{ ...btnGrad, opacity: guardando ? 0.6 : 1, flex: esMobile ? 1 : undefined }}>
                {guardando ? 'Guardando...' : editandoId ? 'Guardar cambios' : 'Crear usuario'}
              </button>
              <button type="button" onClick={() => { setMostrarForm(false); setEditandoId(null); }} style={{ ...btnGhost, flex: esMobile ? 1 : undefined }}>Cancelar</button>
            </div>
          </form>
        )}

        {/* Buscador */}
        <input style={{ ...inputStyle, marginBottom: '16px', maxWidth: esMobile ? '100%' : '360px' }} placeholder="🔍 Buscar por nombre o email..." value={filtro} onChange={e => setFiltro(e.target.value)} />

        {/* Lista */}
        {esMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {usuariosFiltrados.length === 0 ? (
              <div style={{ color: '#BE84A3', textAlign: 'center', padding: '48px', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #FBCFE8' }}>No se encontraron usuarios</div>
            ) : usuariosFiltrados.map(u => (
              <div key={u.id} style={{ background: '#FFFFFF', border: `1px solid ${estaCongelado(u) ? '#BFDBFE' : '#FBCFE8'}`, borderRadius: '14px', padding: '16px', boxShadow: '0 2px 12px rgba(236,72,153,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ color: '#581C47', fontWeight: 600, fontSize: '15px', marginBottom: '2px' }}>{u.nombre}</p>
                    <p style={{ color: '#BE84A3', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</p>
                    {u.telefono && <p style={{ color: '#BE84A3', fontSize: '12px' }}>📱 {u.telefono}</p>}
                  </div>
                  <span style={{ background: '#FCE7F3', color: u.rol === 'admin' ? '#DB2777' : '#EC4899', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, border: '1px solid #FBCFE8', flexShrink: 0 }}>{u.rol.toUpperCase()}</span>
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <BadgeCongelado u={u} />
                  <BadgeForzarPwd u={u} />
                </div>

                {u.rol === 'alumno' && (
                  <div style={{ marginBottom: '10px' }}>
                    {u.accesos?.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {u.accesos.map(a => (
                          <span key={`${a.tipo}-${a.nivel}`} style={{ background: a.tipo === 'militar' ? '#EDE9FE' : '#FCE7F3', color: a.tipo === 'militar' ? '#7C3AED' : '#DB2777', borderRadius: '12px', padding: '3px 10px', fontSize: '11px', fontWeight: 600 }}>
                            {a.tipo === 'militar' ? '🎖️' : '🎓'} {NIVEL_LABEL[a.nivel] || a.nivel}
                          </span>
                        ))}
                      </div>
                    ) : <span style={{ color: '#F59E0B', fontSize: '12px', fontWeight: 600 }}>⚠️ Sin accesos</span>}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button onClick={() => toggleActivo(u)} style={{ background: u.activo ? '#34D39918' : 'rgba(248,113,113,0.1)', border: `1px solid ${u.activo ? '#34D39944' : 'rgba(248,113,113,0.3)'}`, color: u.activo ? '#34D399' : '#F87171', padding: '5px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                    {u.activo ? '✓ Activo' : '✗ Inactivo'}
                  </button>
                  {u.rol === 'alumno' && (
                    <button onClick={() => toggleSuscripcion(u)} style={{ background: u.suscripcion_activa ? '#FCE7F3' : '#FFF0F5', border: '1px solid #FBCFE8', color: u.suscripcion_activa ? '#EC4899' : '#BE84A3', padding: '5px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                      {u.suscripcion_activa ? '💳 Pagado' : '— Sin pago'}
                    </button>
                  )}
                  <button onClick={() => iniciarEdicion(u)} style={{ background: '#FCE7F3', border: '1px solid #FBCFE8', color: '#EC4899', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <PencilSquareIcon style={{ width: 13, height: 13 }} /> Editar
                  </button>
                  {u.id !== usuario?.id && (
                    <>
                      {estaCongelado(u)
                        ? <button onClick={() => descongelar(u.id)} title="Descongelar cuenta" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>☀️</button>
                        : <button onClick={() => { setCongelarModal(u); setCongelarHoras(24); setCongelarMotivo(''); }} title="Congelar cuenta" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>🧊</button>
                      }
                      <button onClick={() => forzarPassword(u.id, u.nombre)} title="Forzar cambio de contraseña" style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#B45309', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>🔑</button>
                      <button onClick={() => eliminar(u.id)} style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '5px 8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <TrashIcon style={{ width: 13, height: 13 }} />
                      </button>
                      {u.rol === 'alumno' && (
                        <button onClick={() => { setGdprModal(u); setGdprConfirm(''); setGdprError(''); }} title="Borrado completo GDPR" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '5px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>GDPR</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="table-scroll">
            <table style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #FBCFE8', background: '#FFF0F5' }}>
                  {['Usuario', 'Rol', 'Accesos', 'Estado', 'Suscripción', 'Acciones'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', color: '#BE84A3', fontSize: '12px', textAlign: 'left', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #FCE7F3', background: estaCongelado(u) ? '#F0F9FF' : undefined }}>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ color: '#581C47', fontWeight: 600, fontSize: '14px' }}>{u.nombre}</p>
                      <p style={{ color: '#BE84A3', fontSize: '12px' }}>{u.email}</p>
                      {u.telefono && <p style={{ color: '#BE84A3', fontSize: '12px' }}>📱 {u.telefono}</p>}
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                        <BadgeCongelado u={u} />
                        <BadgeForzarPwd u={u} />
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: '#FCE7F3', color: u.rol === 'admin' ? '#DB2777' : '#EC4899', borderRadius: '6px', padding: '2px 10px', fontSize: '11px', fontWeight: 700, border: '1px solid #FBCFE8' }}>{u.rol.toUpperCase()}</span>
                    </td>
                    <td style={{ padding: '12px 16px', maxWidth: '220px' }}>
                      {u.rol === 'alumno' ? (
                        u.accesos?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {u.accesos.map(a => (
                              <span key={`${a.tipo}-${a.nivel}`} style={{ background: a.tipo === 'militar' ? '#EDE9FE' : '#FCE7F3', color: a.tipo === 'militar' ? '#7C3AED' : '#DB2777', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
                                {a.tipo === 'militar' ? '🎖️' : '🎓'} {NIVEL_LABEL[a.nivel] || a.nivel}
                              </span>
                            ))}
                          </div>
                        ) : <span style={{ color: '#F59E0B', fontSize: '12px', fontWeight: 600 }}>⚠️ Sin accesos</span>
                      ) : <span style={{ color: '#BE84A3', fontSize: '12px' }}>Acceso total</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => toggleActivo(u)} style={{ background: u.activo ? '#34D39918' : 'rgba(248,113,113,0.1)', border: `1px solid ${u.activo ? '#34D39944' : 'rgba(248,113,113,0.3)'}`, color: u.activo ? '#34D399' : '#F87171', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                        {u.activo ? '✓ Activo' : '✗ Inactivo'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {u.rol === 'alumno' && (
                        <button onClick={() => toggleSuscripcion(u)} style={{ background: u.suscripcion_activa ? '#FCE7F3' : '#FFF0F5', border: '1px solid #FBCFE8', color: u.suscripcion_activa ? '#EC4899' : '#BE84A3', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                          {u.suscripcion_activa ? '💳 Pagado' : '— Sin pago'}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <button onClick={() => iniciarEdicion(u)} style={{ background: '#FCE7F3', border: '1px solid #FBCFE8', color: '#EC4899', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <PencilSquareIcon style={{ width: 13, height: 13 }} /> Editar
                        </button>
                        {u.id !== usuario?.id && (
                          <>
                            {estaCongelado(u)
                              ? <button onClick={() => descongelar(u.id)} title="Descongelar" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', padding: '6px 8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>☀️</button>
                              : <button onClick={() => { setCongelarModal(u); setCongelarHoras(24); setCongelarMotivo(''); }} title="Congelar cuenta" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', padding: '6px 8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>🧊</button>
                            }
                            <button onClick={() => forzarPassword(u.id, u.nombre)} title="Forzar cambio de contraseña" style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#B45309', padding: '6px 8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>🔑</button>
                            <button onClick={() => eliminar(u.id)} style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '6px 8px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              <TrashIcon style={{ width: 14, height: 14 }} />
                            </button>
                            {u.rol === 'alumno' && (
                              <button onClick={() => { setGdprModal(u); setGdprConfirm(''); setGdprError(''); }} title="Borrado completo GDPR" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', padding: '6px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>GDPR</button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {usuariosFiltrados.length === 0 && <div style={{ color: '#BE84A3', textAlign: 'center', padding: '48px' }}>No se encontraron usuarios</div>}
          </div>
        )}
      </div>

      {/* ── MODAL CONGELAR ──────────────────────────────────── */}
      {congelarModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(88,28,71,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setCongelarModal(null)}>
          <div style={{ background: '#FFFFFF', borderRadius: '20px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(88,28,71,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#1D4ED8', fontSize: '18px', marginBottom: '6px' }}>🧊 Congelar cuenta</h3>
            <p style={{ color: '#BE84A3', fontSize: '13px', marginBottom: '20px' }}>
              La cuenta de <strong style={{ color: '#581C47' }}>{congelarModal.nombre}</strong> quedará bloqueada durante el tiempo indicado y su sesión activa se cerrará.
            </p>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Duración</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[{ h: 1, l: '1h' }, { h: 6, l: '6h' }, { h: 24, l: '1 día' }, { h: 48, l: '2 días' }, { h: 168, l: '7 días' }].map(op => (
                  <button key={op.h} type="button" onClick={() => setCongelarHoras(op.h)} style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: congelarHoras === op.h ? '2px solid #1D4ED8' : '2px solid #BFDBFE', background: congelarHoras === op.h ? '#1D4ED8' : '#EFF6FF', color: congelarHoras === op.h ? '#fff' : '#1D4ED8' }}>
                    {op.l}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#BE84A3', fontSize: '12px', display: 'block', marginBottom: '6px' }}>Motivo (opcional)</label>
              <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: '#F0F9FF', border: '1px solid #BFDBFE', color: '#1E3A5F', fontSize: '14px' }} placeholder="Ej: Comportamiento inapropiado en el foro" value={congelarMotivo} onChange={e => setCongelarMotivo(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={confirmarCongelar} disabled={congelando} style={{ flex: 1, background: '#1D4ED8', border: 'none', color: '#fff', padding: '11px', borderRadius: '50px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: congelando ? 0.6 : 1 }}>
                {congelando ? 'Congelando...' : '🧊 Confirmar'}
              </button>
              <button onClick={() => setCongelarModal(null)} style={{ flex: 1, background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', padding: '11px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer' }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL GDPR ──────────────────────────────────────── */}
      {gdprModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(127,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setGdprModal(null)}>
          <div style={{ background: '#FFFFFF', borderRadius: '20px', padding: '28px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 60px rgba(127,0,0,0.4)', border: '2px solid rgba(248,113,113,0.4)' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>⚠️</div>
              <h3 style={{ color: '#991B1B', fontSize: '18px', marginBottom: '6px' }}>Borrado completo GDPR</h3>
              <p style={{ color: '#7F1D1D', fontSize: '13px' }}>
                Esta acción eliminará <strong>todos los datos</strong> de <strong>{gdprModal.nombre}</strong> de forma permanente e irreversible: cuenta, sesiones, accesos, evaluaciones y pagos.
              </p>
            </div>
            {gdprError && <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: '8px', padding: '10px', color: '#991B1B', fontSize: '13px', marginBottom: '14px' }}>{gdprError}</div>}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#7F1D1D', fontSize: '13px', display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Escribe el email <code style={{ background: '#FEF2F2', padding: '2px 6px', borderRadius: '4px' }}>{gdprModal.email}</code> para confirmar
              </label>
              <input
                style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', background: '#FEF2F2', border: '2px solid rgba(248,113,113,0.4)', color: '#991B1B', fontSize: '14px', fontFamily: 'monospace' }}
                placeholder={gdprModal.email}
                value={gdprConfirm}
                onChange={e => setGdprConfirm(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={ejecutarGDPR}
                disabled={ejecutandoGdpr || gdprConfirm !== gdprModal.email}
                style={{ flex: 1, background: gdprConfirm === gdprModal.email ? '#DC2626' : '#FCA5A5', border: 'none', color: '#fff', padding: '11px', borderRadius: '50px', fontSize: '14px', fontWeight: 700, cursor: gdprConfirm === gdprModal.email ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
              >
                {ejecutandoGdpr ? 'Borrando...' : '🗑 Borrar todos los datos'}
              </button>
              <button onClick={() => setGdprModal(null)} style={{ flex: 1, background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', padding: '11px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer' }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
